import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '@/context/auth';
import SupabaseVerification from './SupabaseVerification';
import { testDatabaseAccess } from '@/utils/dbTest';
import { Button } from '../ui/button';
import { Database, RefreshCw, Server } from 'lucide-react';
import { EscortProfile } from '@/types';
import { AdminUser } from './AdminTabs';
import { Report } from './ReportedContent';
import { VerificationRequest } from './VerificationRequests';

interface AdminToolsProps {
  isImporting?: boolean;
  isSynchronizing?: boolean;
  onImportData?: () => Promise<void>;
  onSyncAccounts?: () => Promise<void>;
  escorts?: EscortProfile[];
  onImageUpload?: (profileId: string, imageFile: File) => Promise<string | void | boolean>;
  users?: AdminUser[];
  onUpdateUserStatus?: (userId: string, status: 'active' | 'suspended' | 'pending') => void;
  reportedContent?: Report[];
  onUpdateReportStatus?: (reportId: string, status: string) => void;
  verificationRequests?: VerificationRequest[];
  onVerify?: (requestId: string, status: 'approved' | 'rejected') => void;
}

const AdminTools: React.FC<AdminToolsProps> = ({
  isImporting,
  isSynchronizing,
  onImportData,
  onSyncAccounts,
  escorts,
  onImageUpload,
  users,
  onUpdateUserStatus,
  reportedContent,
  onUpdateReportStatus,
  verificationRequests,
  onVerify
}) => {
  const { user } = useAuth();
  const [dbStatus, setDbStatus] = React.useState<{ success: boolean; counts?: Record<string, number>; error?: unknown } | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const checkDatabase = async () => {
    setIsLoading(true);
    try {
      const result = await testDatabaseAccess();
      setDbStatus(result);
    } catch (error) {
      console.error('Error checking database:', error);
      setDbStatus({ success: false, error: error });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Outils d'administration</CardTitle>
          <CardDescription>
            Vous devez être connecté pour accéder aux outils d'administration.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Tabs defaultValue="verification">
      <TabsList className="grid grid-cols-2 mb-4">
        <TabsTrigger value="verification">
          <Database className="h-4 w-4 mr-2" />
          Vérification Supabase
        </TabsTrigger>
        <TabsTrigger value="database">
          <Server className="h-4 w-4 mr-2" />
          Test Base de données
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="verification">
        <SupabaseVerification />
      </TabsContent>
      
      <TabsContent value="database">
        <Card>
          <CardHeader>
            <CardTitle>Test de la base de données</CardTitle>
            <CardDescription>
              Vérifiez l'accès aux tables de la base de données Supabase.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dbStatus ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg border ${dbStatus.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <h3 className="text-sm font-medium">Statut</h3>
                    <p className={`text-lg font-semibold ${dbStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                      {dbStatus.success ? 'Succès' : 'Échec'}
                    </p>
                    {dbStatus.counts && (
                      <div className="mt-2 space-y-1 text-sm">
                        <p>Profils: {dbStatus.counts.profiles}</p>
                        <p>Photos: {dbStatus.counts.photos}</p>
                        <p>Services: {dbStatus.counts.services}</p>
                        <p>Tarifs: {dbStatus.counts.rates}</p>
                      </div>
                    )}
                  </div>
                  
                  {dbStatus.error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h3 className="text-sm font-medium text-red-600">Erreur</h3>
                      <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-auto">
                        {JSON.stringify(dbStatus.error, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  <Button onClick={checkDatabase} disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Actualisation...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Actualiser
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-center text-muted-foreground mb-4">
                    Cliquez sur le bouton ci-dessous pour tester l'accès à la base de données.
                  </p>
                  <Button onClick={checkDatabase} disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Test en cours...
                      </>
                    ) : (
                      <>
                        <Server className="mr-2 h-4 w-4" />
                        Tester la base de données
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

export default AdminTools;
